import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Pencil, Eye, EyeOff, Globe, ArchiveRestore, Archive, BarChart3, Star, Trash2, Copy, ScanEye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { api, formatMoney, formatDate } from '../../lib/api';
import {
  useScrollShadow, STICKY_CELL, STICKY_HEAD_BG, STICKY_CELL_BG, STICKY_SHADOW,
} from '../../lib/sticky-column';

const STATUS_STYLES = {
  draft:     'bg-slate-100 text-slate-700',
  published: 'bg-emerald-100 text-emerald-700',
  archived:  'bg-rose-100 text-rose-600',
};

export default function AdminCourses() {
  const [courses, setCourses] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const navigate = useNavigate();
  const [tableRef, shadowed] = useScrollShadow();

  const refresh = () => {
    setLoading(true);
    api('/api/admin/courses')
      .then((d) => setCourses(d.courses || []))
      .finally(() => setLoading(false));
  };

  useEffect(refresh, []);

  const filtered = filter === 'all' ? courses : courses.filter((c) => c.status === filter);

  const togglePublish = async (course) => {
    const next = course.status === 'published' ? 'draft' : 'published';
    setBusyId(course.id);
    try {
      await api(`/api/admin/courses/${course.id}`, { method: 'PATCH', body: { status: next } });
      refresh();
    } finally {
      setBusyId(null);
    }
  };

  const toggleFeatured = async (course) => {
    setBusyId(course.id);
    try {
      await api(`/api/admin/courses/${course.id}`, { method: 'PATCH', body: { is_featured: !course.is_featured } });
      refresh();
    } finally {
      setBusyId(null);
    }
  };

  const archive = async (course) => {
    if (!confirm(`Archive "${course.title}"? Existing learners keep access; new purchases blocked.`)) return;
    setBusyId(course.id);
    try {
      await api(`/api/admin/courses/${course.id}`, { method: 'DELETE' });
      refresh();
    } finally {
      setBusyId(null);
    }
  };

  const duplicateCourse = async (course) => {
    setBusyId(course.id);
    try {
      const result = await api(`/api/admin/courses/${course.id}/duplicate`, { method: 'POST' });
      navigate(`/admin/courses/${result.course.id}/edit`);
    } catch (e) {
      alert(e.message);
    } finally {
      setBusyId(null);
    }
  };

  const deleteCourse = async (course) => {
    if (!confirm(`Permanently delete "${course.title}"? This will also remove all enrollments, purchases, and certificates for this course. This cannot be undone.`)) return;
    setBusyId(course.id);
    try {
      await api(`/api/admin/courses/${course.id}/permanent`, { method: 'DELETE' });
      refresh();
    } catch (e) {
      alert(e.message);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="p-4 lg:p-8 max-w-6xl mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="text-2xl font-bold">Courses</h1>
        <Link
          to="/admin/courses/new"
          className="inline-flex items-center gap-2 bg-brand text-white text-sm px-4 py-2 rounded-lg font-medium hover:bg-brand-dark"
        >
          <Plus size={16} /> New course
        </Link>
      </div>

      <div className="flex gap-1 mb-4 text-sm">
        {[
          { k: 'all',       label: `All (${courses.length})` },
          { k: 'published', label: `Published (${courses.filter((c) => c.status === 'published').length})` },
          { k: 'draft',     label: `Drafts (${courses.filter((c) => c.status === 'draft').length})` },
          { k: 'archived',  label: `Archived (${courses.filter((c) => c.status === 'archived').length})` },
        ].map((f) => (
          <button
            key={f.k}
            onClick={() => setFilter(f.k)}
            className={`px-3 py-1.5 rounded-md font-medium ${
              filter === f.k ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-200'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-slate-500">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center">
            <p className="text-slate-500 mb-3">No courses in this view yet.</p>
            <Link to="/admin/courses/new" className="text-brand font-medium hover:underline">
              Create your first course →
            </Link>
          </div>
        ) : (
          <div ref={tableRef} className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="text-left px-5 py-3 font-semibold">Title</th>
                <th className="text-left px-3 py-3 font-semibold hidden md:table-cell">Slug</th>
                <th className="text-left px-3 py-3 font-semibold">Price</th>
                <th className="text-left px-3 py-3 font-semibold">Status</th>
                <th className="text-left px-3 py-3 font-semibold hidden lg:table-cell">Updated</th>
                <th className={`text-right px-5 py-3 font-semibold ${STICKY_CELL} ${STICKY_HEAD_BG} ${shadowed ? STICKY_SHADOW : ''}`}>Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((c) => (
                <tr key={c.id} className="group hover:bg-slate-50">
                  <td className="px-5 py-3 font-medium text-slate-900">{c.title}</td>
                  <td className="px-3 py-3 hidden md:table-cell text-slate-500 font-mono text-xs">{c.slug}</td>
                  <td className="px-3 py-3 text-slate-700">
                    {c.price_cents === 0 ? <span className="text-emerald-600 font-semibold">Free</span> : formatMoney(c.price_cents, c.currency)}
                  </td>
                  <td className="px-3 py-3">
                    <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${STATUS_STYLES[c.status] || ''}`}>
                      {c.status}
                    </span>
                  </td>
                  <td className="px-3 py-3 hidden lg:table-cell text-slate-500 text-xs">{formatDate(c.updated_at)}</td>
                  <td className={`px-5 py-3 text-right ${STICKY_CELL} ${STICKY_CELL_BG} ${shadowed ? STICKY_SHADOW : ''}`}>
                    <div className="inline-flex items-center gap-1">
                      <button
                        onClick={() => toggleFeatured(c)}
                        disabled={busyId === c.id}
                        title={c.is_featured ? 'Remove from featured' : 'Feature on homepage'}
                        className={`p-1.5 rounded disabled:opacity-30 ${
                          c.is_featured
                            ? 'text-amber-500 hover:text-amber-600 hover:bg-amber-50'
                            : 'text-slate-400 hover:text-amber-500 hover:bg-amber-50'
                        }`}
                      >
                        <Star size={14} fill={c.is_featured ? 'currentColor' : 'none'} />
                      </button>
                      <a
                        href={`/courses/${c.slug}`}
                        target="_blank"
                        rel="noreferrer"
                        title="View public page"
                        className="p-1.5 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded"
                      >
                        <Globe size={14} />
                      </a>
                      <a
                        href={`/courses/${c.slug}?preview=1`}
                        target="_blank"
                        rel="noreferrer"
                        title="Preview course page"
                        className="p-1.5 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded"
                      >
                        <ScanEye size={14} />
                      </a>
                      <Link
                        to={`/admin/courses/${c.id}/edit`}
                        title="Edit"
                        className="p-1.5 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded"
                      >
                        <Pencil size={14} />
                      </Link>
                      <Link
                        to={`/admin/courses/${c.id}/reports`}
                        title="Reports"
                        className="p-1.5 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded"
                      >
                        <BarChart3 size={14} />
                      </Link>
                      <button
                        onClick={() => duplicateCourse(c)}
                        disabled={busyId === c.id}
                        title="Duplicate"
                        className="p-1.5 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded disabled:opacity-30"
                      >
                        <Copy size={14} />
                      </button>
                      <button
                        onClick={() => togglePublish(c)}
                        disabled={busyId === c.id || c.status === 'archived'}
                        title={c.status === 'published' ? 'Move to draft' : 'Publish'}
                        className="p-1.5 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded disabled:opacity-30"
                      >
                        {c.status === 'published' ? <EyeOff size={14} /> : <Eye size={14} />}
                      </button>
                      <button
                        onClick={() => archive(c)}
                        disabled={busyId === c.id || c.status === 'archived'}
                        title="Archive"
                        className="p-1.5 text-slate-500 hover:text-rose-600 hover:bg-rose-50 rounded disabled:opacity-30"
                      >
                        <Archive size={14} />
                      </button>
                      <button
                        onClick={() => deleteCourse(c)}
                        disabled={busyId === c.id}
                        title="Delete permanently"
                        className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded disabled:opacity-30"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  );
}
